import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import customPublicationApi from "../api/customPublicationApi"
import Spinner from "../components/spinner"
import { getTimeAgo } from "../utils/getTimeAgo"
import ImageWithFallback from "../helpers/ImageWithFallback"
import { IoImageOutline } from "react-icons/io5"

const PublicationDetailPage = () => {
  const { publicationId } = useParams()
  const [publication, setPublication] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchPublication = async () => {
      setLoading(true)
      setError(null)
      try {
        console.log(`Fetching publication with ID: ${publicationId}...`)
        const response = await customPublicationApi.getById(publicationId)
        console.log("Publication detail response:", response)
        if (response.data) {
          setPublication(response.data)
        } else {
          throw new Error("Publication not found.")
        }
      } catch (err) {
        console.error("Error fetching publication details:", err)
        setError(
          err.response?.data?.detail ||
            err.message ||
            "Failed to load publication."
        )
      } finally {
        setLoading(false)
      }
    }

    fetchPublication()
  }, [publicationId])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return <div></div>
  }

  if (!publication) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 text-lg">Publication not found.</p>
      </div>
    )
  }

  // Ensure thumbnail URL is absolute
  const thumbnailUrl = publication.thumbnail?.startsWith("http")
    ? publication.thumbnail
    : `${process.env.REACT_APP_API_URL}${publication.thumbnail}`

  return (
    <main className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {publication.title}
            </h1>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Published {getTimeAgo(publication.created_at)}
            </span>
          </div>

          {/* Image Section */}
          {thumbnailUrl && (
            <div className="w-full max-h-96 overflow-hidden flex justify-center items-center bg-gray-100 dark:bg-gray-700">
              <ImageWithFallback
                src={thumbnailUrl}
                className="object-contain max-h-96 w-auto"
                fallback={
                  <IoImageOutline className="w-24 h-24 text-gray-400 dark:text-gray-500" />
                }
              />
            </div>
          )}

          {/* Content Section */}
          <div className="px-6 py-8">
            <div
              className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: publication.content }}
            />
            {/* Note: Using dangerouslySetInnerHTML requires trusting the source of publication.content */}
          </div>
        </article>
      </div>
    </main>
  )
}

export default PublicationDetailPage
